import {Trans} from '@lingui/macro';
import React, {FC, useContext} from 'react';

import {Handler, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {styled} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {PushContext} from '../../../../../srcCommon/push/pushContext';
import {DropdownItemExternalLinkIcon} from '../../../common/dropdowns/dropdownItemExternalLinkIcon';
import {DropdownSeparator} from '../../../common/dropdowns/dropdownSeparator';
import {PermissionDropdownItem} from '../../../common/dropdowns/permissionDropdownItem';
import {PermissionSimpleDropdownItem} from '../../../common/dropdowns/permissionSimpleDropdownItem';
import {NavigationLineLabel} from '../../../common/navigation/navigationLineLabel';
import {NavigationLineStandardIcon} from '../../../common/navigation/navigationLineStandardIcon';
import {WorkspaceDragSourceTypesEnum} from '../dnd/workspaceDragDropTypes';
import {EditSidebarCopy} from '../editor/copy/edit-sidebar-copy';
import {HideInSidebarCopy} from '../editor/copy/hide-in-sidebar-copy';
import {WorkspacesEditorRarelyViewedBadge} from '../editor/workspacesEditorRarelyViewedBadge';
import {WorkspaceLineDropTarget} from '../line/workspaceLineDropTarget';
import {WorkspaceLoadingTypesEnum} from '../line/workspaceLineMore';
import {WorkspaceLineMoreContainer} from '../line/workspaceLineMoreContainer';
import {makeInteractionId} from '../workspaceCustomViewItem';
import {TeamWorkspaceCustomViewItemContainerProps} from './teamWorkspaceCustomViewItemContainer';

interface TeamWorkspaceCustomViewItemProps extends TeamWorkspaceCustomViewItemContainerProps {
  /** Is the currently selected item with its conversations visible in P2/P3. Not relevant to edit mode. */
  isSelected: boolean;
  isUnreadBadge: boolean;
  badgeValue: number;
  isUnread: boolean;
  loadingType?: WorkspaceLoadingTypesEnum;
  onEditClick: Handler;
  onHideClick: Handler;
  hasEditPermission: boolean;
  onEditWorkspaces: Handler;
  onClick?: Handler;
  /** Should the item be checked while in edit mode. */
  isChecked?: boolean;
  /** Is the workspace in edit mode. */
  isEditingWorkspace?: boolean;
  isRecentlyViewed?: boolean;
}

/*
 * Style.
 */

const StyledLabelDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTextDiv = styled.div`
  flex-shrink: 1;
  min-width: 0;
`;

const StyledPermissionSimpleDropdownItem = styled(PermissionSimpleDropdownItem)`
  width: 140px;
`;

/*
 * Component.
 */

export const TeamWorkspaceCustomViewItem: FC<TeamWorkspaceCustomViewItemProps> = (props) => {
  const {customView, isChecked, isEditingWorkspace} = props;
  const {isConnected} = useContext(PushContext);

  // Context menu.
  const renderMoreMenu: RendererOf<PopoverTransferProps> = (popoverProps) =>
    renderDropdown(popoverProps, props);

  const isDisabled = !isConnected;

  // Workspace line item.
  return (
    <WorkspaceLineDropTarget
      {...props}
      indentLevel={customView.nestingLevel}
      renderTooltip={() => customView.tag.name}
      isDisabled={isDisabled}
      interactionId={makeInteractionId(customView, Boolean(isEditingWorkspace))}
      workspaceDragItem={{
        type: WorkspaceDragSourceTypesEnum.TEAM_VIEW,
        hasExpandedChildren: customView.children.length > 0,
        id: customView.id,
        nestingLevel: customView.nestingLevel,
        namespace: customView.namespace,
        isLast: props.isLastInList,
      }}
      shouldHidePreviewBadge={isEditingWorkspace}
    >
      {renderIconOrEmoji(props)}
      <NavigationLineLabel {...props}>
        <StyledLabelDiv>
          <StyledTextDiv>
            <TooltipOverflowCondition>{customView.tag.name}</TooltipOverflowCondition>
          </StyledTextDiv>
          {props.isRecentlyViewed === false && <WorkspacesEditorRarelyViewedBadge />}
        </StyledLabelDiv>
      </NavigationLineLabel>
      <WorkspaceLineMoreContainer
        {...props}
        renderMenu={isEditingWorkspace ? undefined : renderMoreMenu}
        isDisabled={isDisabled}
        isChecked={isChecked}
        shouldShowOfflineIcon={isDisabled}
        shouldRemoveBadgeLeftSpacing={props.isRecentlyViewed === false}
      />
    </WorkspaceLineDropTarget>
  );
};

/*
 * Helpers.
 */

function renderIconOrEmoji(props: TeamWorkspaceCustomViewItemProps) {
  const {isSelected} = props;
  const iconName: IconName = isSelected ? 'customViewsFilledSmall' : 'customViewsSmall';
  return <NavigationLineStandardIcon iconName={iconName} />;
}

function renderDropdown(popoverProps: PopoverTransferProps, props: TeamWorkspaceCustomViewItemProps) {
  return (
    <SimpleDropdown {...popoverProps}>
      {props.hasEditPermission && (
        <PermissionDropdownItem onSelect={props.onEditClick}>
          <DropdownItemContent>
            <Trans id="customviewmenu-editview">Edit view</Trans>
          </DropdownItemContent>
          <DropdownItemExternalLinkIcon />
        </PermissionDropdownItem>
      )}

      <StyledPermissionSimpleDropdownItem onSelect={props.onHideClick}>
        <HideInSidebarCopy />
      </StyledPermissionSimpleDropdownItem>

      <DropdownSeparator />
      <PermissionSimpleDropdownItem onSelect={props.onEditWorkspaces}>
        <EditSidebarCopy />
      </PermissionSimpleDropdownItem>
    </SimpleDropdown>
  );
}
